import mermaid from 'mermaid'

let initialized = false
let renderCounter = 0
let lastMermaidTheme: 'dark' | 'default' | null = null

function resolveMermaidTheme(): 'dark' | 'default' {
  if (typeof document === 'undefined') return 'default'
  return document.documentElement.classList.contains('dark') ? 'dark' : 'default'
}

/** Shared mermaid.initialize() fields. VSIX Maater Preview must stay in sync (`cursor-extension/src/preview/mermaid-preview.ts`). */
export function mermaidInitializeOptions(theme: 'dark' | 'default') {
  return {
    startOnLoad: false,
    theme,
    securityLevel: 'loose' as const,
    // Mermaid's default inserts a full-page "bomb" Syntax error SVG into the DOM.
    // We catch failures and render our own inline error UI instead.
    suppressErrorRendering: true,
  }
}

export function ensureMermaidInitialized(): void {
  const theme = resolveMermaidTheme()
  if (initialized && lastMermaidTheme === theme) return
  mermaid.initialize(mermaidInitializeOptions(theme))
  initialized = true
  lastMermaidTheme = theme
}

export type MermaidRenderResult =
  | { ok: true; svg: string }
  | { ok: false; error: string }

/** True when Mermaid returned its built-in error diagram SVG (bomb + "Syntax error"). */
export function isMermaidErrorSvg(svgHtml: string): boolean {
  const lower = svgHtml.toLowerCase()
  if (lower.includes('syntax error in text')) return true
  // Theme CSS always defines `.error-icon` / `.error-text` - only match real error nodes.
  if (/<g[^>]*class="[^"]*error-icon[^"]*"/i.test(svgHtml)) return true
  if (/<text[^>]*class="[^"]*error-text[^"]*"/i.test(svgHtml)) return true
  return false
}

/** Short, user-facing message from a Mermaid throw / parse failure. */
export function formatMermaidError(err: unknown): string {
  const raw = err instanceof Error ? err.message : String(err)
  const cleaned = raw
    .replace(/\s*mermaid version [\d.]+\s*/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  if (!cleaned || /^syntax error in text$/i.test(cleaned)) {
    return 'This diagram could not be rendered (invalid Mermaid syntax).'
  }
  // Keep messages readable; avoid dumping huge parse stacks into the UI.
  if (cleaned.length > 280) {
    return `${cleaned.slice(0, 277)}...`
  }
  return cleaned
}

/**
 * Undo HTML pipeline damage before Mermaid parse.
 * Preview/VSIX may leave entities (`--&gt;`) or `<br/>` tags in fence text;
 * Cursor's native preview often strips those and still renders.
 */
export function normalizeMermaidSource(source: string): string {
  let text = source.replace(/^\uFEFF/, '').replace(/\u200b/g, '')
  // Named + numeric entities commonly left by markdown→HTML→text round trips.
  text = text
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/&#x27;/gi, "'")
    .replace(/&#x2[fF];/g, '/')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
  // Line breaks in labels: prefer real newlines (htmlLabels still ok with <br>).
  text = text.replace(/<br\s*\/?>/gi, '\n')
  return text.trim()
}

/** Render Mermaid source to SVG HTML string. */
export async function renderMermaidDiagram(source: string): Promise<MermaidRenderResult> {
  ensureMermaidInitialized()
  const text = normalizeMermaidSource(source)
  if (!text) {
    return { ok: false, error: 'Empty diagram' }
  }
  try {
    const id = `mermaid-${++renderCounter}`
    const { svg } = await mermaid.render(id, text)
    if (isMermaidErrorSvg(svg)) {
      return {
        ok: false,
        error: 'This diagram could not be rendered (invalid Mermaid syntax).',
      }
    }
    return { ok: true, svg: normalizeMermaidSvg(svg) }
  } catch (err: unknown) {
    // Remove any error nodes Mermaid may have left in the document (belt + suspenders).
    removeOrphanedMermaidErrorNodes()
    return { ok: false, error: formatMermaidError(err) }
  }
}

/** Best-effort cleanup if an older Mermaid build still injects error SVGs. */
export function removeOrphanedMermaidErrorNodes(): void {
  if (typeof document === 'undefined') return
  for (const el of Array.from(document.querySelectorAll('svg'))) {
    if (!isMermaidErrorSvg(el.outerHTML)) continue
    // Only remove floating/orphaned error diagrams, not ones inside our blocks
    // (we never put error SVGs in .mermaid-diagram when handling fails ourselves).
    if (el.closest('.mermaid-block')) continue
    el.remove()
  }
}

/** Make rendered SVG responsive inside our containers. */
export function normalizeMermaidSvg(svgHtml: string): string {
  const doc = new DOMParser().parseFromString(svgHtml, 'image/svg+xml')
  const svgEl = doc.querySelector('svg')
  if (svgEl) {
    svgEl.removeAttribute('height')
    svgEl.style.maxWidth = '100%'
    svgEl.style.height = 'auto'
    return svgEl.outerHTML
  }
  return svgHtml
}

/** Data URL for lightbox `<img>` so pan/zoom gets intrinsic dimensions (like raster images). */
export function mermaidSvgToDataUrl(svgHtml: string): string {
  const encoded = encodeURIComponent(svgHtml)
    .replace(/'/g, '%27')
    .replace(/"/g, '%22')
  return `data:image/svg+xml;charset=utf-8,${encoded}`
}
